import { Injectable } from '@angular/core'
import { addDays, addMonths, addYears, format, getDate, getDay, getMonth, getYear, isDate, isValid, parse, startOfWeek } from 'date-fns'
import { enGB } from 'date-fns/locale'
import { NativeDateAdapter } from '@angular/material/core'

@Injectable()
export class NgxMatDateFnsAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: string): string {
    return format(date, displayFormat)
  }

  override parse(value: any, parseFormat: string): Date | null {
    const parsedDate = parse(value, parseFormat, new Date())
    return isValid(parsedDate) ? parsedDate : null
  }

  override toIso8601(date: Date): string {
    return date.toISOString()
  }

  override isValid(date: Date): boolean {
    return isDate(date) && isValid(date)
  }

  override invalid(): Date {
    return new Date(NaN)
  }

  override addCalendarDays(date: Date, days: number): Date {
    return addDays(date, days)
  }

  override addCalendarMonths(date: Date, months: number): Date {
    return addMonths(date, months)
  }

  override addCalendarYears(date: Date, years: number): Date {
    return addYears(date, years)
  }

  override getYear(date: Date): number {
    return getYear(date)
  }

  override getMonth(date: Date): number {
    return getMonth(date)
  }

  override getDate(date: Date): number {
    return getDate(date)
  }

  override getDayOfWeek(date: Date): number {
    return getDay(date)
  }

  getHour(date: Date): number {
    return date.getHours()
  }

  getMinute(date: Date): number {
    return date.getMinutes()
  }

  getSecond(date: Date): number {
    return date.getSeconds()
  }

  // TODO Replace with a date-fns implementation when the API allows it - return type would need to be Date over void
  setHour(date: Date, value: number) {
    date.setHours(value)
  }

  // TODO Replace with a date-fns implementation when the API allows it - return type would need to be Date over void
  setMinute(date: Date, value: number) {
    date.setMinutes(value)
  }

  // TODO Replace with a date-fns implementation when the API allows it - return type would need to be Date over void
  setSecond(date: Date, value: number) {
    date.setSeconds(value)
  }

  override clone(date: Date): Date {
    return new Date(date.getTime())
  }

  override today(): Date {
    return new Date()
  }

  override getFirstDayOfWeek(): number {
    return 1
  }

  override getNumDaysInMonth(date: Date): number {
    const nextMonth = addMonths(date, 1)
    const startOfNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1)
    const lastDayOfCurrentMonth = addDays(startOfNextMonth, -1)
    return getDate(lastDayOfCurrentMonth)
  }

  override getYearName(date: Date): string {
    return getYear(date).toString()
  }

  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const formatString = style === 'long' ? 'MMMM' : style === 'short' ? 'MMM' : 'MMMM'
    const months = []
    for (let i = 0; i < 12; i++) {
      const date = new Date(2000, i, 1)
      months.push(format(date, formatString))
    }
    return months
  }

  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => (i + 1).toString())
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const start = startOfWeek(new Date(), { weekStartsOn: 0, locale: enGB })
    const days = []

    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i)
      switch (style) {
        case 'narrow':
          days.push(format(day, 'EEEEEE', { locale: enGB })) // One letter ("M", "T", etc.)
          break
        case 'short':
          days.push(format(day, 'EEE', { locale: enGB })) // Three letters ("Mon", "Tue", etc.)
          break
        case 'long':
          days.push(format(day, 'EEEE', { locale: enGB })) // Full name ("Monday", "Tuesday", etc.)
          break
      }
    }
    return days
  }

  override createDate(year: number, month: number, date: number): Date {
    const newDate = new Date(year, month, date)
    if (this.isValid(newDate)) {
      return newDate
    }
    return this.invalid()
  }
}
